// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { generateCloudinaryUrl, GenerateCloudinaryUrlOptions } from "@stoneage/XDL/Utilities/getCloudinaryImageUrl";
import { getSettingsCollection } from "@stoneage/App/Store/Selectors/SettingsSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

/**
 * Background-image url wrapper, used to avoid breaking the url from special chars
 * @param imageUrl image url
 * @param options
 * @returns formatted/escaped url
 */
export const fetchBackgroundImg = (imageUrl: string, options?: GenerateCloudinaryUrlOptions) =>
    `url('${generateCloudinaryUrl(imageUrl, options)}')`;

export type FocalPoint =
    | "topLeft"
    | "topCenter"
    | "topRight"
    | "centerLeft"
    | "center"
    | "centerRight"
    | "bottomLeft"
    | "bottomCenter"
    | "bottomRight";

const focalPointToObjectPosition: Record<FocalPoint, string> = {
    topLeft: "left top",
    topCenter: "center top",
    topRight: "right top",
    centerLeft: "left center",
    center: "center center",
    centerRight: "right center",
    bottomLeft: "left bottom",
    bottomCenter: "center bottom",
    bottomRight: "right bottom",
};

/**
 * The OOTB focal point helper emits `background-position`, which only works on a background-image.
 * Widgets that render a real <img> (LazyImage / LazyPicture) need `object-position` instead.
 * @param focalPoint the widget focalPoint field
 * @returns an `object-position` css declaration
 */
export const getObjectPositionStyles = (focalPoint?: FocalPoint) =>
    `object-position: ${(focalPoint && focalPointToObjectPosition[focalPoint]) || "center center"};`;

export const getNotFoundImagesSettings = (state: ApplicationState) => {
    const customSettings = getSettingsCollection(state).customSettings;
    return {
        generalNotFoundImagePath: customSettings.cloudinaryGlobalNotFoundImagePath,
        notFoundCategoryImagePath:
            customSettings.productMediumDefaultImagePath ||
            customSettings.productLargeDefaultImagePath ||
            customSettings.productSmallDefaultImagePath,
    };
};
